// src/pages/VerifyOTP.tsx
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import AuthShell from '../components/auth/AuthShell';
import { ErrorNote, OtpBoxes, PrimaryButton, fa, maskPhone, useCountdown } from '../components/auth/authUi';

const RESEND_SECONDS = 90;

const VerifyOTP: React.FC = () => {
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const { left, restart } = useCountdown(RESEND_SECONDS);

    const phoneNumber: string | null = (location.state as any)?.phoneNumber || localStorage.getItem('temp_phone');

    useEffect(() => {
        if (!phoneNumber) navigate('/login');
    }, [phoneNumber, navigate]);

    const verify = async (value = code) => {
        if (!phoneNumber || value.length !== 6) return;
        setError('');
        setLoading(true);

        try {
            const response = await authAPI.verifyOTP(phoneNumber, value);
            if (response.token && response.buyer) {
                localStorage.removeItem('temp_phone');
                login(response.token, response.buyer);
                navigate('/dashboard', { replace: true });
            } else {
                setError('پاسخ نامعتبر از سرور');
            }
        } catch (err: any) {
            // interceptor پیام سرور را در err.message می‌گذارد، نه در err.response
            setError(err?.message || 'کد تأیید نامعتبر است. دوباره تلاش کنید.');
            setCode('');
        } finally {
            setLoading(false);
        }
    };

    const resend = async () => {
        if (!phoneNumber) return;
        setError('');
        setLoading(true);
        try {
            await authAPI.requestOTP(phoneNumber);
            restart();
            setCode('');
        } catch (err: any) {
            setError(err?.message || 'ارسال دوباره‌ی کد ناموفق بود.');
        } finally {
            setLoading(false);
        }
    };

    if (!phoneNumber) return null;

    return (
        <AuthShell
            eyebrow="ورود"
            title="کد ورود را وارد کنید"
            subtitle={`کد ۶ رقمی به ${maskPhone(phoneNumber)} پیامک شد.`}
            footer={
                <Link to="/login" className="inline-flex items-center gap-1 text-stone-600 hover:text-stone-900">
                    <ArrowRight className="h-4 w-4" />
                    تغییر شماره
                </Link>
            }
        >
            <div className="space-y-6">
                <OtpBoxes
                    value={code}
                    onChange={(v) => {
                        setCode(v);
                        if (error) setError('');
                    }}
                    onComplete={(v) => verify(v)}
                    disabled={loading}
                    invalid={Boolean(error)}
                />

                {error && <ErrorNote>{error}</ErrorNote>}

                <PrimaryButton onClick={() => verify()} loading={loading} disabled={code.length !== 6}>
                    <span className="inline-flex items-center gap-2">
                        <Check className="h-4 w-4" />
                        ورود به پنل
                    </span>
                </PrimaryButton>

                <div className="text-center text-sm">
                    {left > 0 ? (
                        <span className="tabular-nums text-stone-400">
                            ارسال دوباره تا {fa(Math.floor(left / 60))}:{fa(left % 60).padStart(2, '۰')}
                        </span>
                    ) : (
                        <button
                            type="button"
                            onClick={resend}
                            disabled={loading}
                            className="font-semibold text-emerald-700 hover:text-emerald-900"
                        >
                            ارسال دوباره‌ی کد
                        </button>
                    )}
                </div>
            </div>
        </AuthShell>
    );
};

export default VerifyOTP;
